<script setup lang="ts">
import { ArrowLeft, ArrowUpRight, Clock3, Pause, Play, RefreshCw, RotateCcw, Workflow, X } from '@lucide/vue'
import { EXECUTION_CLOSE_REFUSALS, executionCloseRefusal, executionIsOpen } from '@zerohuman/shared'
import {
    Button,
    Separator,
    ZhConfirmButton,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusDot,
    ZhTooltip,
} from '@zerohuman/ui'
import { computed, nextTick, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { api, type ExecutionRun, formatDuration, runStoppedLine } from '@/api'
import ExecutionGraph from '@/components/ExecutionGraph.vue'
import ExecutionResources from '@/components/ExecutionResources.vue'
import ExecutionStatus from '@/components/ExecutionStatus.vue'
import MemberAvatar from '@/components/MemberAvatar.vue'
import { useLiveResource } from '@/composables/useLiveResource'
import { formatUsdCost } from '@/lib/cost'
import { EXECUTION_PAUSED_HOLD, executionAttentionNote, executionStoppedNote } from '@/lib/executionStop'
import { executionPillStatus, runPillStatus } from '@/lib/statusTone'

const route = useRoute()
const selectedTaskId = ref<string | null>(null)
const inspectorHeading = ref<{
    focus: (options?: FocusOptions) => void
    scrollIntoView: (options?: ScrollIntoViewOptions) => void
} | null>(null)
const {
    data: execution,
    error,
    loading,
    refreshing,
    updatedAt,
    now,
    visible,
    refresh,
} = useLiveResource(
    () => String(route.params.id),
    signal => api.execution(String(route.params.id), signal),
    // Every three seconds, as for the board and the run page; the elapsed times still tick every second.
    { everyMs: 3000 },
)
watch(
    () => route.params.id,
    () => {
        selectedTaskId.value = null
    },
)
const selectedTask = computed(() => execution.value?.graph.nodes.find(node => node.id === selectedTaskId.value) ?? null)
const shownRuns = computed(() => {
    const runs = execution.value?.runs ?? []
    const taskId = selectedTask.value?.taskId ?? selectedTask.value?.id
    return selectedTask.value ? runs.filter(run => run.taskId === taskId) : runs
})
const attentionNote = computed(() => (execution.value ? executionAttentionNote(execution.value) : null))
const completedCount = computed(() => execution.value?.runs.filter(run => run.status === 'succeeded').length ?? 0)
const duration = computed(() => {
    if (!execution.value) return '—'
    const end = execution.value.finishedAt ? new Date(execution.value.finishedAt).getTime() : now.value
    return formatDuration(Math.max(0, end - new Date(execution.value.startedAt).getTime()))
})
const stale = computed(() => !!error.value || (updatedAt.value !== null && now.value - updatedAt.value > 5000))
/**
 * Stopping the whole execution (#348). Cancelling one run only ever stopped that run; its queued and
 * waiting siblings carried on spending, so the action lives on the execution, guarded by the same
 * confirm dialog every other destructive console action uses.
 */
const cancelling = ref(false)
const cancelError = ref<string | null>(null)
// Which statuses still have work to stop is a fact about `executionStatus()`, not a list this view gets to
// guess: `executionIsOpen` keeps it next to the `ExecutionStatus` union that defines it (a not-yet-started
// execution reports `queued`, so it is cancellable too). A `blocked` one in the Complete lane is off Blockers and
// reads "Stopped": a dead end has nothing open, and a cleared run was already let go, so there is nothing to cancel.
const cancellable = computed(
    () => !!execution.value && executionIsOpen(execution.value.status) && execution.value.lane !== 'complete',
)
const cancelledNote = computed(() => {
    const detail = execution.value
    if (!detail?.cancelledAt) return null
    const when = new Date(detail.cancelledAt).toLocaleString()
    // A task that stopped its own execution is named with its reason, not shown as a person's cancel.
    const stopped = executionStoppedNote(detail)
    if (stopped) return `${stopped} · ${when}`
    return detail.cancelledBy ? `Cancelled by ${detail.cancelledBy.name} · ${when}` : `Cancelled ${when}`
})
async function cancelExecution(): Promise<void> {
    const detail = execution.value
    if (!detail) return
    cancelling.value = true
    cancelError.value = null
    try {
        await api.cancelExecution(detail.id)
        await refresh()
    } catch {
        cancelError.value = 'Could not cancel this execution. It may have already finished.'
    } finally {
        cancelling.value = false
    }
}
/**
 * Closing a finished execution takes it off the board (#557); Reopen puts it back. Only a Complete execution can
 * be closed — the API refuses anything queued, running, waiting or still on Blockers, with the same reason
 * `executionCloseRefusal` gives here — and the record of who closed it stays after a reopen.
 */
const closeBusy = ref(false)
const closeError = ref<string | null>(null)
const closable = computed(() => !!execution.value && !execution.value.closed && !executionCloseRefusal(execution.value))
const stamp = (label: { name: string } | null, at: string) =>
    `${label ? `by ${label.name} · ` : ''}${new Date(at).toLocaleString()}`
const closedNote = computed(() => {
    const detail = execution.value
    if (!detail?.closedAt) return null
    if (detail.closed) return `Closed ${stamp(detail.closedBy, detail.closedAt)}`
    const reopened = detail.reopenedAt ? `Reopened ${stamp(detail.reopenedBy, detail.reopenedAt)} · ` : ''
    return `${reopened}Last closed ${stamp(detail.closedBy, detail.closedAt)}`
})
async function setClosed(close: boolean): Promise<void> {
    const detail = execution.value
    if (!detail) return
    closeBusy.value = true
    closeError.value = null
    try {
        await (close ? api.closeExecution(detail.id) : api.reopenExecution(detail.id))
        await refresh()
    } catch (failure) {
        // A close is refused when the execution moved on since this page last polled (a run failed, a retry
        // started): show the API's own reason, and re-read the execution so the page matches it.
        const said = failure instanceof Error ? failure.message : ''
        const refusal = Object.values(EXECUTION_CLOSE_REFUSALS).find(message => message === said)
        closeError.value = refusal ?? `Could not ${close ? 'close' : 'reopen'} this execution. Try again.`
        await refresh()
    } finally {
        closeBusy.value = false
    }
}
/**
 * Holding the whole execution (#809): Pause lets the step in progress finish and holds everything else, and Resume,
 * in its place once it is paused, lets it go on. Neither throws work away, so neither asks to confirm. Cancel still
 * shows on a paused execution. The page shows the execution the call returns; a failed call re-reads it.
 */
const pausing = ref(false)
const pauseError = ref<string | null>(null)
const paused = computed(() => execution.value?.status === 'paused')
const pausedNote = computed(() => {
    const detail = execution.value
    if (detail?.status !== 'paused' || !detail.pausedAt) return null
    return `Paused ${stamp(detail.pausedBy ?? null, detail.pausedAt)}`
})
/**
 * The execution's record of its pauses and resumes (#809), so the page still says who held it, who let it go and when
 * once it runs again. The pause standing now is `pausedNote`'s; this line holds the ones before it.
 */
const pauseHistoryNote = computed(() => {
    const detail = execution.value
    const history = detail?.pauseHistory ?? []
    const past = detail?.status === 'paused' ? history.slice(0, -1) : history
    if (past.length === 0) return null
    return past
        .map(entry => `${entry.action === 'paused' ? 'Paused' : 'Resumed'} ${stamp(entry.by, entry.at)}`)
        .join(' · ')
})
async function setPaused(pause: boolean): Promise<void> {
    const detail = execution.value
    if (!detail) return
    pausing.value = true
    pauseError.value = null
    try {
        execution.value = await (pause ? api.pauseExecution(detail.id) : api.resumeExecution(detail.id))
    } catch (failure) {
        pauseError.value = pauseFailure(pause ? 'pause' : 'resume', failure)
        await refresh()
    } finally {
        pausing.value = false
    }
}
/** Why a pause or resume did not happen. A refusal says so: it is never a sign the execution has finished. */
function pauseFailure(verb: 'pause' | 'resume', failure: unknown): string {
    const status = (failure as { status?: unknown } | null)?.status
    if (status === 401) return `Your session has expired. Sign in again to ${verb} this execution.`
    if (status === 403) return `You do not have permission to ${verb} this execution.`
    return `Could not ${verb} this execution. Check your connection and try again.`
}
async function selectTask(id: string) {
    selectedTaskId.value = id
    await nextTick()
    inspectorHeading.value?.focus({ preventScroll: true })
    inspectorHeading.value?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
}
function runDuration(run: ExecutionRun) {
    if (!run.startedAt)
        return run.notBefore ? `Scheduled ${new Date(run.notBefore).toLocaleTimeString()}` : 'Not started'
    return formatDuration(
        Math.max(
            0,
            (run.finishedAt ? new Date(run.finishedAt).getTime() : now.value) - new Date(run.startedAt).getTime(),
        ),
    )
}
function sourceLabels(run: ExecutionRun) {
    const detail = execution.value
    if (!detail) return []
    return detail.links
        .filter(link => link.targetRunId === run.id)
        .map(link => ({
            id: link.sourceRunId,
            retry: link.kind === 'retry',
            name: detail.runs.find(source => source.id === link.sourceRunId)?.task.name ?? 'Previous run',
        }))
}
</script>

<template>
    <section class="execution-detail w-full min-w-0 space-y-5 p-4 md:p-8">
        <RouterLink to="/executions" class="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm"><ArrowLeft :size="15" /> Executions</RouterLink>
        <p v-if="loading && !execution" role="status" class="text-muted-foreground py-16 text-center text-sm">Loading execution…</p>
        <div v-if="error" role="alert" class="text-destructive flex flex-wrap items-center justify-between gap-3 text-sm">
            <span>{{ execution ? 'Live updates interrupted. The last received state is shown below.' : 'Could not load this execution. It may no longer be available.' }}</span>
            <Button variant="outline" size="sm" :disabled="refreshing" @click="refresh">Try again</Button>
        </div>
        <template v-if="execution">
            <!-- The title takes the width left beside the actions and wraps there; below `lg` the actions sit under it, left aligned. -->
            <header class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div class="min-w-0 flex-1 space-y-2">
                    <div class="flex flex-wrap items-center gap-3"><ZhPageTitle kicker="Execution">{{ execution.name }}</ZhPageTitle><ExecutionStatus :status="executionPillStatus(execution)" /></div>
                    <p class="text-muted-foreground text-xs"><span class="font-mono">{{ execution.id.slice(0, 8) }}</span> · Started {{ new Date(execution.startedAt).toLocaleString() }}<span v-if="execution.startingTask.name !== execution.name"> · {{ execution.startingTask.name }}</span><span v-if="execution.lineage === 'standalone'"> · No linked handoffs recorded yet</span></p>
                    <!-- Who asked for the work (#1373), or that nobody did: a schedule, a webhook, a token, or older work. -->
                    <p class="text-muted-foreground text-xs" data-testid="execution-requested-by">Asked for by <span v-if="execution.requestedBy" class="text-foreground font-medium">{{ execution.requestedBy.name }}</span><span v-else>nobody</span></p>
                    <p v-if="pausedNote" class="text-muted-foreground text-xs" data-testid="execution-paused-note">{{ pausedNote }}<span class="block" data-testid="execution-paused-hold">{{ EXECUTION_PAUSED_HOLD }}</span></p>
                    <p v-if="pauseHistoryNote" class="text-muted-foreground text-xs" data-testid="execution-pause-history">{{ pauseHistoryNote }}</p>
                    <p v-if="cancelledNote" class="text-muted-foreground text-xs" data-testid="execution-cancelled-note">{{ cancelledNote }}</p>
                    <p v-if="closedNote" class="text-muted-foreground text-xs" data-testid="execution-closed-note">{{ closedNote }}</p>
                    <p v-if="execution.onBlockers" class="text-muted-foreground text-xs" data-testid="execution-on-blockers">One of its runs is on <RouterLink to="/blockers" class="hover:text-foreground underline underline-offset-2">Blockers</RouterLink>. It stays in the Blocked lane until that run is cleared or retried.</p>
                </div>
                <div class="flex shrink-0 flex-col items-start gap-3 lg:items-end">
                    <div class="flex flex-wrap items-center gap-3">
                        <Button v-if="execution.closed" variant="outline" size="sm" :disabled="closeBusy" @click="setClosed(false)">Reopen</Button>
                        <Button v-else-if="closable" variant="outline" size="sm" :disabled="closeBusy" @click="setClosed(true)">Close execution</Button>
                        <Button v-if="cancellable && paused" variant="outline" size="sm" :disabled="pausing" @click="setPaused(false)"><Play :size="15" /> {{ pausing ? 'Resuming…' : 'Resume' }}</Button>
                        <Button v-else-if="cancellable" variant="outline" size="sm" :disabled="pausing" @click="setPaused(true)"><Pause :size="15" /> {{ pausing ? 'Pausing…' : 'Pause' }}</Button>
                        <ZhConfirmButton
                            v-if="cancellable"
                            label="Cancel execution"
                            title="Stop this execution?"
                            description="Every run in it that is running, queued, waiting or not yet started stops, their open questions close, and nothing new starts."
                            confirm-label="Stop everything"
                            :disabled="cancelling"
                            @confirm="cancelExecution"
                        />
                        <div class="lg:text-right"><span class="text-muted-foreground flex items-center gap-1.5 lg:justify-end text-xs"><ZhStatusDot :state="stale ? 'active' : 'complete'" :pulse="visible" />{{ stale ? 'Reconnecting…' : visible ? 'Live · every second' : 'Updates paused' }}</span><span v-if="updatedAt" class="text-muted-foreground mt-1 block text-xs">Updated {{ new Date(updatedAt).toLocaleTimeString() }}</span></div>
                        <Button variant="outline" size="icon-sm" aria-label="Refresh execution" :disabled="refreshing" @click="refresh"><RefreshCw :size="15" /></Button>
                    </div>
                    <!-- Every link any of its runs added (an issue, a pull request, a preview), top right where the work is acted on. -->
                    <ExecutionResources :resources="execution.resources" class="sm:max-w-md lg:justify-end" />
                </div>
            </header>
            <ZhPanel class="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs">
                <span class="flex items-center gap-2"><Clock3 :size="15" class="text-muted-foreground" /><strong class="font-medium tabular-nums">{{ duration }}</strong></span>
                <span><strong class="font-medium">{{ execution.runCount }}</strong><span class="text-muted-foreground"> runs · {{ completedCount }} completed</span></span>
                <span><strong class="font-medium">{{ execution.taskCount }}</strong><span class="text-muted-foreground"> tasks visited</span></span>
                <span class="text-muted-foreground">{{ execution.teams.length }} team{{ execution.teams.length === 1 ? '' : 's' }} · {{ execution.members.length }} member{{ execution.members.length === 1 ? '' : 's' }}</span>
                <span class="text-muted-foreground ml-auto tabular-nums">{{ formatUsdCost(execution.costUsdCents) }}</span>
            </ZhPanel>
            <p v-if="pauseError" class="text-destructive text-sm" role="alert" data-testid="execution-pause-error">{{ pauseError }}</p>
            <p v-if="cancelError" class="text-destructive text-sm" role="alert">{{ cancelError }}</p>
            <p v-if="closeError" class="text-destructive text-sm" role="alert" data-testid="execution-close-error">{{ closeError }}</p>
            <!--
                A stop that later work on the same issue or pull request has moved past is not this page's to act on:
                it says where the work went on and how that stands, rather than asking for attention (#79 follow-up).
            -->
            <ZhPanel v-if="execution.continuedIn" as="div" class="flex flex-wrap items-center gap-2 text-sm" role="status" data-testid="execution-continued"><span><strong class="font-medium">This work went on in another execution.</strong> Its latest task is {{ execution.continuedIn.task.name }}:</span><ExecutionStatus :status="execution.continuedIn.status" /><RouterLink :to="`/executions/${execution.continuedIn.runId}`" class="text-primary inline-flex items-center gap-1 font-medium">Open it <ArrowUpRight :size="13" /></RouterLink></ZhPanel>
            <ZhPanel v-else-if="attentionNote" as="div" class="flex items-center gap-2 text-sm" role="status" data-testid="execution-attention"><ZhStatusDot state="active" :pulse="false" class="shrink-0" /><span><strong class="font-medium">Execution needs attention.</strong> {{ attentionNote }}</span></ZhPanel>
            <!--
                The graph and the inspector sit side by side from `lg` and stack below it. The graph's panel takes the
                kit canvas height; the inspector fills that same row height from `lg` (absolutely, so its run list
                never stretches the row) and scrolls on its own, and below `lg` it is capped and scrolls the same way.
            -->
            <div class="flex flex-col gap-4 lg:flex-row">
                <ZhPanel canvas flush class="min-w-0 lg:flex-1"><ExecutionGraph :execution="execution" :selected-task-id="selectedTaskId" @select="selectTask" /></ZhPanel>
                <div class="relative lg:w-75 lg:shrink-0">
                    <ZhPanel as="aside" flush class="max-h-120 overflow-y-auto lg:absolute lg:inset-0 lg:max-h-none" aria-label="Execution run history">
                        <header class="flex items-start justify-between gap-2 p-4"><div class="min-w-0"><ZhSectionHeading ref="inspectorHeading" tabindex="-1">{{ selectedTask ? selectedTask.name : 'Run history' }}</ZhSectionHeading><p class="text-muted-foreground mt-1 text-xs">{{ selectedTask ? `${shownRuns.length} runs of this task` : 'Every handoff, in order' }}</p></div><Button v-if="selectedTask" type="button" variant="ghost" size="icon-xs" aria-label="Show all execution runs" @click="selectedTaskId = null"><X :size="15" /></Button></header>
                        <Separator />
                        <template v-if="selectedTask">
                            <div class="space-y-2 p-4 text-xs">
                                <p v-if="selectedTask.goal" class="text-muted-foreground">{{ selectedTask.goal }}</p>
                                <div class="flex flex-wrap gap-2"><RouterLink v-if="selectedTask.team" :to="`/teams/${selectedTask.team.id}`" class="text-muted-foreground hover:text-foreground underline underline-offset-2">{{ selectedTask.team.name }}</RouterLink><ZhTooltip v-if="selectedTask.role" text="Current role assignment"><RouterLink :to="`/roles/${selectedTask.role.id}`" class="text-muted-foreground hover:text-foreground underline underline-offset-2">{{ selectedTask.role.name }}</RouterLink></ZhTooltip></div>
                                <RouterLink v-if="selectedTask.taskId && !selectedTask.missing" :to="`/tasks/${selectedTask.taskId}`" class="text-primary inline-flex items-center gap-1 font-medium">Open task <ArrowUpRight :size="13" /></RouterLink>
                            </div>
                            <Separator />
                        </template>
                        <div v-if="shownRuns.length === 0" class="text-muted-foreground p-5 text-sm"><Workflow :size="24" class="mb-3" /><p>This route has not been taken.</p><p class="mt-1 text-xs">It remains visible so you can see where this execution could go next.</p></div>
                        <ol v-else class="execution-run-list divide-y" aria-label="Task runs">
                            <li v-for="run in shownRuns" :key="run.id" class="space-y-2 p-4" :data-run-id="run.id">
                                <div v-if="sourceLabels(run).length" class="text-muted-foreground flex flex-col gap-1 text-xs"><span v-for="source in sourceLabels(run)" :key="source.id" class="flex items-center gap-1"><RotateCcw v-if="source.retry" :size="10" /><span>{{ source.retry ? 'Retry of' : 'From' }} <RouterLink :to="`/runs/${source.id}`" class="hover:text-foreground underline underline-offset-2">{{ source.name }}</RouterLink></span></span></div>
                                <div class="flex items-start justify-between gap-2"><RouterLink :to="`/runs/${run.id}`" class="hover:text-primary text-sm font-medium leading-snug">{{ run.task.name }}</RouterLink><ArrowUpRight :size="13" class="text-muted-foreground shrink-0" /></div>
                                <div class="flex flex-wrap items-center justify-between gap-2"><ZhTooltip :text="runStoppedLine(run)"><ExecutionStatus :status="runPillStatus(run)" /></ZhTooltip><span class="text-muted-foreground text-xs tabular-nums">{{ runDuration(run) }}</span></div>
                                <div class="text-muted-foreground flex items-center gap-2 text-xs"><MemberAvatar v-if="run.member" :member="run.member" class="size-5" fallback-class="text-xs" /><RouterLink v-if="run.member" :to="`/members/${run.member.id}`" class="hover:text-foreground">{{ run.member.name }}</RouterLink><span v-else>OS</span><ZhTooltip v-if="run.team" :text="run.team.name"><span class="truncate">· {{ run.team.name }}</span></ZhTooltip></div>
                                <p v-if="run.blockedReason && run.resumesByItself" class="text-muted-foreground break-words text-xs" data-testid="execution-run-retrying">Retrying by itself: {{ run.blockedReason }}</p>
                                <p v-else-if="run.blockedReason" class="text-destructive break-words text-xs">{{ run.blockedReason }}</p>
                                <ZhTooltip :text="new Date(run.createdAt).toLocaleString()"><span class="text-muted-foreground block text-xs">{{ new Date(run.createdAt).toLocaleTimeString() }} · <span class="font-mono">{{ run.id.slice(0, 8) }}</span></span></ZhTooltip>
                            </li>
                        </ol>
                    </ZhPanel>
                </div>
            </div>
            <footer class="text-muted-foreground flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                <span>Recorded handoffs and retries form this execution.</span><span>Unvisited routes use current task definitions.</span>
                <div v-if="execution.roles.length" class="flex flex-wrap items-center gap-2"><span>Current roles:</span><RouterLink v-for="role in execution.roles" :key="role.id" :to="`/roles/${role.id}`" class="hover:text-foreground underline underline-offset-2">{{ role.name }}</RouterLink></div>
            </footer>
        </template>
    </section>
</template>
